import React, { useMemo, useRef } from 'react'
import {
  GraphIcon,
  OrderIcon,
  HelpIcon,
  LegalIcon,
  StoreIcon,
  EditIcon,
  InsightIcon,
} from '@instacart/ids-core'
import { type unstableFeatureHighlight } from '@instacart/ids-tooling'
import { useHistory } from 'react-router-dom'
import { useParams } from 'react-router'
import { useDomainMessages } from 'domains/pickup-settings/utils/domain/intl'
import ko from 'legacy/common/knowledgeOwl'
import { useNavContext } from 'gin-and-tonic/containers/nav/utils/NavContext'
import { ROUTES_BY_NAME } from 'utils/routing/routes'
import { url } from 'utils/parsing/url'
import { type TourStep, type useTourValues } from 'utils/tour/tour.types'
import { useRPPTour } from 'utils/tour/tour.hooks'
import { fontWeight } from 'gin-and-tonic/foundation/fontWeight'
import { useDomainGoToPath as useShiftsDomainGoToPath } from 'domains/shifts/utils/domain/routing'
import { useDomainGoToPath as usePickupSettingsDomainGoToPath } from 'domains/pickup-settings/utils/domain/routing'
import { usePickupTourStatus } from 'domains/pickup-settings/training-tour/usePickupTourStatus'
import pic7 from 'domains/pickup-settings/assets/onboarding/welcome-tour-pic7.png'
import pic6 from 'domains/pickup-settings/assets/onboarding/welcome-tour-pic6.png'
import pic5 from 'domains/pickup-settings/assets/onboarding/welcome-tour-pic5.png'
import pic4 from 'domains/pickup-settings/assets/onboarding/welcome-tour-pic4.png'
import pic3 from 'domains/pickup-settings/assets/onboarding/welcome-tour-pic3.png'
import pic2 from 'domains/pickup-settings/assets/onboarding/welcome-tour-pic2.png'
import pic1 from 'domains/pickup-settings/assets/onboarding/welcome-tour-pic1.png'
import { Status, useFeatureTourUpdateMutation } from 'domains/pickup-settings/api'

type StepConfig = {
  selector: string
  folder: string
  imgUrl?: string
  imgAlt?: string
  beaconPosition?: unstableFeatureHighlight.BeaconPosition
  titleIcon?: React.ReactNode
  hasDetailedTour?: boolean
  showPrevButton?: boolean
}

export function addOffset({
  beaconPosition = 'topLeft',
  height: POPOVER_HEIGHT,
  top,
}: {
  beaconPosition?: unstableFeatureHighlight.BeaconPosition
  height: number
  top: number
}): [number, number] {
  const NAV_BAR_WIDTH = 280
  const MARGIN_TOP = 30
  const OFFSET_BY_BEACON_POSITION: Partial<
    Record<unstableFeatureHighlight.BeaconPosition, number>
  > = {
    topLeft: MARGIN_TOP + top,
    middleLeft: MARGIN_TOP + top - POPOVER_HEIGHT / 2,
    bottomLeft: MARGIN_TOP + top - POPOVER_HEIGHT,
  }

  return [NAV_BAR_WIDTH, OFFSET_BY_BEACON_POSITION[beaconPosition]]
}

export function usePickupRemindMeLaterTour() {
  const { isTourPending } = usePickupTourStatus('pickup_overview')
  const { isOpen, setSteps } = useRPPTour()
  const { closeNav, openNav, setFolderControlledByTour } = useNavContext()
  const goToSettingsStepConfig = useGoToSettingsStep()
  const messages = useDomainMessages({
    title: 'pickupSettingsDomain.tours.welcomeTour.step7.title',
    'description.points': 'pickupSettingsDomain.tours.welcomeTour.step7.description.points',
  })
  const { selector, imgUrl, beaconPosition, folder } =
    WELCOME_STEPS_CONFIGS[WELCOME_STEPS_CONFIGS.length - 1]
  const getPositionsRef = useRef(() => document.querySelector(selector)?.getBoundingClientRect().y)

  const steps: TourStep[] = [
    {
      selector,
      imgUrl,
      beaconPosition,
      position: ({ height }) =>
        addOffset({ height, top: getPositionsRef.current(), beaconPosition }),
      showStepper: false,
      onEnterStep: () => {
        openNav()
        setFolderControlledByTour(folder)
        ko.showKnowledgeOwl()
      },
      onClickPrevStep: () => {
        ko.showKnowledgeOwl()
      },
      onClickNextStep: () => {
        ko.showKnowledgeOwl()
        closeNav()
      },
      onExitTour: ({ setIsOpen, setCurrentStep }) => {
        closeNav()
        ko.showKnowledgeOwl()
        setIsOpen(true)
        setCurrentStep(1)
      },
      title: (
        <div css={titleCss}>
          <HelpIcon />
          <span>{messages.title}</span>
        </div>
      ),
      description: (
        <div>
          <ul css={listContainerCss}>
            {messages['description.points'].split('\n').map(text => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        </div>
      ),
      nextButtonLabel: 'Done',
    },
    goToSettingsStepConfig,
  ]

  const startTour = () => {
    if (!isOpen && isTourPending) {
      setSteps(steps)
    }
  }

  return { steps, startTour }
}

function useGoToSettingsStep(): TourStep {
  const goToPickupSettings = usePickupSettingsDomainGoToPath('pickup-settings')
  const messages = useDomainMessages({
    title: 'pickupSettingsDomain.tours.remindMeLaterStep.goToSettings.title',
    description: 'pickupSettingsDomain.tours.remindMeLaterStep.goToSettings.description',
    btnLabel: 'pickupSettingsDomain.tours.remindMeLaterStep.goToSettings.nextBtnLabel',
  })

  const goToSettingsStep: TourStep = useMemo(
    () => ({
      selector: '#pickupWelcomeTour',
      position: [80, 400],
      beaconPosition: 'middleLeft',
      showStepper: false,
      onClickNextStep: () => {
        goToPickupSettings()
      },
      onExitTour: ({ setIsOpen }) => {
        setIsOpen(false)
      },
      title: messages.title,
      description: messages.description,
      nextButtonLabel: messages.btnLabel,
      showPrevButton: false,
    }),
    [messages.btnLabel, messages.description, messages.title, goToPickupSettings]
  )

  return goToSettingsStep
}

const WELCOME_STEPS_CONFIGS: StepConfig[] = [
  {
    folder: 'orders.title',
    selector: '#nav-item-pickup',
    imgUrl: pic1,
    titleIcon: <OrderIcon />,
    hasDetailedTour: true,
    showPrevButton: false,
  },
  {
    folder: 'insightsPortalDomain.nav.folder',
    selector: '#nav-item-pickupAnalytics',
    imgUrl: pic2,
    titleIcon: <InsightIcon />,
    hasDetailedTour: true,
  },
  {
    folder: 'reports.title',
    selector: '#nav-item-reports',
    imgUrl: pic3,
    titleIcon: <GraphIcon />,
    beaconPosition: 'middleLeft',
  },
  {
    folder: 'files.title',
    selector: '#nav-item-tlogs',
    imgUrl: pic4,
    titleIcon: <LegalIcon />,
  },
  {
    folder: 'storeOperations.title',
    selector: '#nav-item-shifts',
    imgUrl: pic5,
    titleIcon: <StoreIcon />,
    beaconPosition: 'middleLeft',
    hasDetailedTour: true,
  },
  {
    folder: 'settings.title',
    selector: '#nav-item-pickupSettings',
    imgUrl: pic6,
    titleIcon: <EditIcon />,
    beaconPosition: 'middleLeft',
  },
  {
    folder: 'help',
    selector: '#nav-item-welcomeTour',
    imgUrl: pic7,
    titleIcon: <HelpIcon />,
    beaconPosition: 'middleLeft',
  },
]

export function usePickupWelcomeTour(): useTourValues {
  const isTourPending = usePickupTourStatus('pickup_overview')
  const params = useParams()
  const { push } = useHistory()
  const { closeNav, openNav, setFolderControlledByTour } = useNavContext()
  const messages = useDomainMessages({
    revisitText: 'pickupSettingsDomain.tours.welcomeTour.revisitText',
    [`${WELCOME_STEPS_CONFIGS[0].selector}.title`]:
      'pickupSettingsDomain.tours.welcomeTour.step1.title',
    [`${WELCOME_STEPS_CONFIGS[0].selector}.description.main`]:
      'pickupSettingsDomain.tours.welcomeTour.step1.description.main',
    [`${WELCOME_STEPS_CONFIGS[0].selector}.description.points`]:
      'pickupSettingsDomain.tours.welcomeTour.step1.description.points',
    [`${WELCOME_STEPS_CONFIGS[1].selector}.title`]:
      'pickupSettingsDomain.tours.welcomeTour.step2.title',
    [`${WELCOME_STEPS_CONFIGS[1].selector}.description.main`]:
      'pickupSettingsDomain.tours.welcomeTour.step2.description.main',
    [`${WELCOME_STEPS_CONFIGS[1].selector}.description.points`]:
      'pickupSettingsDomain.tours.welcomeTour.step2.description.points',
    [`${WELCOME_STEPS_CONFIGS[2].selector}.title`]:
      'pickupSettingsDomain.tours.welcomeTour.step3.title',
    [`${WELCOME_STEPS_CONFIGS[2].selector}.description.main`]:
      'pickupSettingsDomain.tours.welcomeTour.step3.description.main',
    [`${WELCOME_STEPS_CONFIGS[2].selector}.description.points`]:
      'pickupSettingsDomain.tours.welcomeTour.step3.description.points',
    [`${WELCOME_STEPS_CONFIGS[3].selector}.title`]:
      'pickupSettingsDomain.tours.welcomeTour.step4.title',
    [`${WELCOME_STEPS_CONFIGS[3].selector}.description.main`]:
      'pickupSettingsDomain.tours.welcomeTour.step4.description.main',
    [`${WELCOME_STEPS_CONFIGS[3].selector}.description.points`]:
      'pickupSettingsDomain.tours.welcomeTour.step4.description.points',
    [`${WELCOME_STEPS_CONFIGS[4].selector}.title`]:
      'pickupSettingsDomain.tours.welcomeTour.step5.title',
    [`${WELCOME_STEPS_CONFIGS[4].selector}.description.points`]:
      'pickupSettingsDomain.tours.welcomeTour.step5.description.points',
    [`${WELCOME_STEPS_CONFIGS[5].selector}.title`]:
      'pickupSettingsDomain.tours.welcomeTour.step6.title',
    [`${WELCOME_STEPS_CONFIGS[5].selector}.description.points`]:
      'pickupSettingsDomain.tours.welcomeTour.step6.description.points',
    [`${WELCOME_STEPS_CONFIGS[6].selector}.title`]:
      'pickupSettingsDomain.tours.welcomeTour.step7.title',
    [`${WELCOME_STEPS_CONFIGS[6].selector}.description.points`]:
      'pickupSettingsDomain.tours.welcomeTour.step7.description.points',
  })
  const [completeFeatureTour] = useFeatureTourUpdateMutation({
    variables: { name: 'pickup_overview', status: Status.Completed },
  })

  const reachedSettingsPageRef = useRef(false)
  const { isOpen, setSteps } = useRPPTour()
  const goToSettingsStep = useGoToSettingsStep()

  const getPositionsRef = useRef(
    Object.fromEntries(
      WELCOME_STEPS_CONFIGS.map(({ selector }) => [
        selector,
        () => document.querySelector(selector)?.getBoundingClientRect().y,
      ])
    )
  )

  const goToShifts = useShiftsDomainGoToPath('shifts')
  const goToPickupSettings = usePickupSettingsDomainGoToPath('pickup-settings')

  const routingBySelector = {
    [WELCOME_STEPS_CONFIGS[0].selector]: () => push(url(ROUTES_BY_NAME.pickup, params)),
    [WELCOME_STEPS_CONFIGS[1].selector]: () => push(url(ROUTES_BY_NAME['pickupAnalytics'], params)),
    [WELCOME_STEPS_CONFIGS[2].selector]: () =>
      push(url(ROUTES_BY_NAME['reports-normalized-reports-list'], params)),
    [WELCOME_STEPS_CONFIGS[3].selector]: () => push(url(ROUTES_BY_NAME.tlogs, params)),
    [WELCOME_STEPS_CONFIGS[4].selector]: goToShifts,
    [WELCOME_STEPS_CONFIGS[5].selector]: goToPickupSettings,
  }

  const filteredStepConfigs = WELCOME_STEPS_CONFIGS.filter(({ selector }) =>
    document.querySelector(selector) ? true : false
  )

  const steps: TourStep[] = filteredStepConfigs.map(
    (
      {
        selector,
        folder,
        titleIcon,
        imgUrl,
        beaconPosition,
        hasDetailedTour,
        imgAlt,
        showPrevButton = true,
      },
      index,
      self
    ) => ({
      imgUrl,
      imgAlt,
      selector,
      beaconPosition,
      showPrevButton,
      position: ({ height }) =>
        addOffset({ height, top: getPositionsRef.current[selector](), beaconPosition }),
      onEnterStep: () => {
        if (index === 0) openNav()

        setFolderControlledByTour(folder)
        routingBySelector[selector]?.()

        if (selector === '#nav-item-pickupSettings') {
          reachedSettingsPageRef.current = true
        }
        if (index === self.length - 1) {
          ko.showKnowledgeOwl()
          completeFeatureTour()
        }
      },
      onClickNextStep: ({ setCurrentStep, currentStep }) => {
        if (index < self.length - 1) {
          setCurrentStep(currentStep)
          setFolderControlledByTour(self[index + 1].folder)
          setTimeout(() => {
            setCurrentStep(currentStep + 1)
          }, 300)
        } else {
          ko.showKnowledgeOwl()
          closeNav()

          if (!reachedSettingsPageRef.current) {
            setSteps([goToSettingsStep])
          }
        }
      },
      onClickPrevStep: ({ setCurrentStep, currentStep }) => {
        if (index > 0) {
          setCurrentStep(currentStep)
          setFolderControlledByTour(self[index - 1].folder)
          setTimeout(() => {
            setCurrentStep(currentStep - 1)
          }, 300)
        }
      },
      onExitTour: ({ setCurrentStep, setIsOpen }) => {
        if (index < self.length - 1) {
          setIsOpen(true)
          setFolderControlledByTour(self[steps.length - 1].folder)

          setTimeout(() => {
            setCurrentStep(steps.length - 1)
          }, 300)
        } else {
          if (!reachedSettingsPageRef.current) {
            setSteps([goToSettingsStep])
          }
          closeNav()
          ko.showKnowledgeOwl()
        }
      },
      description: (
        <div>
          {messages[`${selector}.description.main`]}
          <ul css={listContainerCss}>
            {messages[`${selector}.description.points`].split('\n').map(pointText => (
              <li key={pointText}>{pointText}</li>
            ))}
          </ul>
          {hasDetailedTour && (
            <strong css={{ fontWeight: fontWeight.BOLD }}>{messages.revisitText}</strong>
          )}
        </div>
      ),
      title: (
        <div css={titleCss}>
          {titleIcon}
          <span>{messages[`${selector}.title`]}</span>
        </div>
      ),
    })
  )

  const startTour = () => {
    if (!isOpen && isTourPending) {
      setSteps(steps)
    }
  }

  return { steps, startTour }
}

const titleCss = {
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
  '> span': {
    marginLeft: '2px',
  },
}

const listContainerCss = {
  listStyle: 'disc',
  padding: '0',
  margin: '0',
  marginLeft: '35px',
  marginBottom: '10px',
}
